import * as jsonpatch from 'jsonpatch'
import { find, flatMap, compact, flow } from 'lodash'
import type { FieldSpec, Schema } from '@wix/wix-data-schema-types'
import { FieldType, PermissionRole } from '@wix/wix-data-schema-types'
import {
  findError,
  isCollectionNotFoundError,
} from '@wix/wix-data-client-common-standalone'
import type {
  CollectionsApi,
  SchemaOperation,
  GetAllOptions,
  CreateCollectionsApiParams,
} from './types'
import { SchemaOperationType } from './types'
import { ensurePermissions } from './permissions'
import normalizeFieldIndices from './normalizeFieldIndices'
import sortFieldsByIndicesAndRemoveIndices from './sortFieldsByIndicesAndRemoveIndices'
import { applyImplicitPublishingPluginFixes } from './plugins/publishing'
import { applyImplicitMultilingualPluginFixes } from './plugins/multilingual'

const isValidSchema = (schema: Schema) => schema && schema.fields

const createInitialSchema = (
  collectionId: string,
  ownerAppId?: string,
  version?: number,
): Schema => ({
  id: collectionId,
  displayName: collectionId,
  displayField: 'title',
  fields: {
    title: {
      displayName: 'Title',
      type: FieldType.text,
      systemField: false,
    },
  },
  permissions: {
    read: PermissionRole.admin,
    update: PermissionRole.admin,
    insert: PermissionRole.admin,
    remove: PermissionRole.admin,
  },
  plugins: {},
  ownerAppId,
  version,
})

const postProcessSchema = flow(
  normalizeFieldIndices,
  sortFieldsByIndicesAndRemoveIndices,
  ensurePermissions,
)

const applyImplicitFixesOnUpdatedSchema = flow(
  applyImplicitPublishingPluginFixes,
  applyImplicitMultilingualPluginFixes,
)

const createCollectionsApi = ({
  transportLayer: {
    loadSchema,
    listSchemas,
    saveSchema,
    removeSchema,
    restoreSchema,
  },
  ownerAppId,
}: CreateCollectionsApiParams): CollectionsApi => {
  const fetchSchema = async (collectionId: string) => {
    const schema = await loadSchema(collectionId)
    if (!isValidSchema(schema)) {
      throw new Error('Invalid schema structure')
    }
    return schema
  }

  const execute = async (operation: SchemaOperation) => {
    const { collectionId, type } = operation
    const patches = flatMap(operation.patches)

    switch (type) {
      case SchemaOperationType.CREATE: {
        const schema = await loadSchema(collectionId).catch(error => {
          if (findError(error, isCollectionNotFoundError)) {
            return
          }
          throw new Error('Failed to load schema for creation', {
            cause: error,
          })
        })
        const version = schema?.isDeleted ? schema.version : undefined
        const createdSchema = normalizeFieldIndices(
          jsonpatch.apply_patch(
            createInitialSchema(collectionId, ownerAppId, version),
            patches,
          ),
        )
        return saveSchema(collectionId, createdSchema)
      }
      case SchemaOperationType.REMOVE:
        return removeSchema(collectionId)

      case SchemaOperationType.RESTORE: {
        return restoreSchema(collectionId)
      }
      case SchemaOperationType.UPDATE: {
        const updatedSchema = normalizeFieldIndices(
          jsonpatch.apply_patch(await fetchSchema(collectionId), patches),
        )
        const { schema } = applyImplicitFixesOnUpdatedSchema({
          schema: updatedSchema,
        })
        return saveSchema(collectionId, schema)
      }
      default:
        throw new Error(`Unknown schema operation: ${type}`)
    }
  }

  const get = async (collectionId: string) => {
    const schema = await loadSchema(collectionId)
    if (!isValidSchema(schema)) {
      throw new Error('Invalid schema structure')
    }
    return postProcessSchema(schema)
  }
  const getSome = async (collectionIds: string[]) => {
    return (await listSchemas())
      .filter(isValidSchema)
      .filter(({ id }) => collectionIds.includes(id))
      .map(postProcessSchema)
  }
  const getAll = async (options?: GetAllOptions) => {
    return (await listSchemas(options))
      .filter(isValidSchema)
      .map(postProcessSchema)
  }
  const getReferring = async (collectionId: string) => {
    const schemas = await listSchemas()
    const referringSchemas = schemas
      .filter(schema => collectionId !== schema.id)
      .filter(schema =>
        find(
          schema.fields,
          (field: FieldSpec) =>
            'referencedCollection' in field &&
            field.referencedCollection === collectionId,
        ),
      )
    return compact(referringSchemas).map(postProcessSchema)
  }

  return {
    execute,
    get,
    getSome,
    getAll,
    getReferring,
  }
}

export default createCollectionsApi
